import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Globe, Lock, Mail, ArrowLeft, Crown } from "lucide-react";
import { AppShell } from "@/components/AppShell";
import { PageHeader } from "@/components/PageHeader";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useIsPremium } from "@/hooks/useIsPremium";
import { createGroup } from "@/lib/groups.functions";
import { cn } from "@/lib/utils";
import { openProDrawer } from "@/components/ProDrawer";
import { isGroupLimitError } from "@/lib/group-limits";

export const Route = createFileRoute("/comunidades/novo")({
  head: () => ({ meta: [{ title: "Criar comunidade — Viny Club" }] }),
  component: NovoGrupo,
});

const privacyOptions = [
  { value: "public" as const, label: "Pública", desc: "Qualquer pessoa encontra e entra", icon: Globe, premium: false },
  { value: "private" as const, label: "Privada", desc: "Visível, mas precisa de aprovação", icon: Lock, premium: true },
  { value: "invite_only" as const, label: "Apenas por convite", desc: "Só entra com link de convite", icon: Mail, premium: true },
];

function NovoGrupo() {
  const { isAuthenticated, loading } = useCurrentUser();
  const { isPremium } = useIsPremium();
  const navigate = useNavigate();
  const createFn = useServerFn(createGroup);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [location, setLocation] = useState("Brasil");
  const [privacy, setPrivacy] = useState<"public" | "private" | "invite_only">("public");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [bannerUrl, setBannerUrl] = useState("");

  const mut = useMutation({
    mutationFn: () =>
      createFn({
        data: {
          name,
          description,
          category,
          location,
          privacy,
          avatar_url: avatarUrl,
          banner_url: bannerUrl,
        },
      }),
    onSuccess: (r) => {
      toast.success("Comunidade criada!");
      navigate({ to: "/comunidades/$slug", params: { slug: r.slug } });
    },
    onError: (e: Error) => {
      toast.error(e.message);
      if (isGroupLimitError(e)) openProDrawer();
    },
  });

  if (!loading && !isAuthenticated) {
    return (
      <AppShell>
        <div className="mx-auto max-w-md px-4 py-20 text-center">
          <h2 className="mb-2 text-xl font-semibold">Entre para criar uma comunidade</h2>
          <Link to="/auth" className="inline-block rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground">
            Entrar
          </Link>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="mx-auto max-w-2xl px-4 pb-16">
        <Link to="/comunidades" className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Voltar
        </Link>
        <PageHeader eyebrow="Comunidade" title="Criar comunidade" subtitle="Defina o tom do seu espaço." />

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim().length < 2) {
              toast.error("Dê um nome à comunidade");
              return;
            }
            if ((privacy === "private" || privacy === "invite_only") && !isPremium) {
              toast.error("Comunidades privadas são exclusivas para embaixadores");
              return;
            }
            mut.mutate();
          }}
          className="space-y-6"
        >
          <div className="overflow-hidden rounded-2xl border border-[color:var(--hairline)] bg-surface/40">
            <div className="relative h-32 w-full overflow-hidden bg-gradient-to-br from-primary/30 via-surface to-surface">
              {bannerUrl && <img loading="lazy" decoding="async" src={bannerUrl} alt="" className="h-full w-full object-cover" />}
            </div>
            <div className="flex items-end gap-3 px-4 pb-4">
              <div className="-mt-8 h-16 w-16 shrink-0 overflow-hidden rounded-2xl border-2 border-background bg-surface">
                {avatarUrl ? (
                  <img loading="lazy" decoding="async" src={avatarUrl} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className="grid h-full w-full place-items-center text-xl font-semibold text-muted-foreground">
                    {name[0]?.toUpperCase() || "?"}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Nome *</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={60}
              placeholder="Ex: Minha vitrola é mais bonita que meu sofá"
              className="h-11 w-full rounded-xl border border-[color:var(--hairline)] bg-surface/60 px-4 text-sm focus:border-primary/60 focus:outline-none"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Descrição</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
              rows={3}
              placeholder="Sobre o que é essa comunidade?"
              className="w-full rounded-xl border border-[color:var(--hairline)] bg-surface/60 px-4 py-3 text-sm focus:border-primary/60 focus:outline-none"
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Categoria</label>
              <input
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                maxLength={40}
                placeholder="Música, Rock, MPB..."
                className="h-11 w-full rounded-xl border border-[color:var(--hairline)] bg-surface/60 px-4 text-sm focus:border-primary/60 focus:outline-none"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Local</label>
              <input
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                maxLength={60}
                placeholder="Brasil, São Paulo..."
                className="h-11 w-full rounded-xl border border-[color:var(--hairline)] bg-surface/60 px-4 text-sm focus:border-primary/60 focus:outline-none"
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">URL do avatar</label>
              <input
                value={avatarUrl}
                onChange={(e) => setAvatarUrl(e.target.value)}
                placeholder="https://..."
                className="h-11 w-full rounded-xl border border-[color:var(--hairline)] bg-surface/60 px-4 text-sm focus:border-primary/60 focus:outline-none"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">URL do banner</label>
              <input
                value={bannerUrl}
                onChange={(e) => setBannerUrl(e.target.value)}
                placeholder="https://..."
                className="h-11 w-full rounded-xl border border-[color:var(--hairline)] bg-surface/60 px-4 text-sm focus:border-primary/60 focus:outline-none"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Privacidade</label>
            <div className="grid gap-2">
              {privacyOptions.map((opt) => {
                const Icon = opt.icon;
                const active = privacy === opt.value;
                const locked = opt.premium && !isPremium;
                const handleClick = () => {
                  if (locked) {
                    navigate({ to: "/settings/billing" });
                    return;
                  }
                  setPrivacy(opt.value);
                };
                return (
                  <button
                    type="button"
                    key={opt.value}
                    onClick={handleClick}
                    className={cn(
                      "flex items-start gap-3 rounded-xl border p-4 text-left transition-colors",
                      active
                        ? "border-primary bg-primary/5"
                        : "border-[color:var(--hairline)] bg-surface/40 hover:bg-surface/70",
                    )}
                  >
                    <Icon className={cn("mt-0.5 h-5 w-5", active ? "text-primary" : "text-muted-foreground")} />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 text-sm font-semibold">
                        {opt.label}
                        {locked && (
                          <span className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/5 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">
                            <Crown className="h-3 w-3" /> Apenas embaixador
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">{opt.desc}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <button
            type="submit"
            disabled={mut.isPending}
            className="h-11 w-full rounded-full bg-primary text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {mut.isPending ? "Criando..." : "Criar comunidade"}
          </button>
        </form>
      </div>
    </AppShell>
  );
}
